// Catalog.ID Client — "Keys on this device", without a DOM
// ============================================================================
// Everything the Devices pane's key group DECIDES, separated from everything it
// draws (device-setup-restructure §9.3). The client's vitest environment has no
// DOM, and this group changes what a browser holds of the member's identity —
// so every decision it makes is testable here, and `devices-pane.ts` next door
// only renders what this module returns.
//
// Three things live here:
//
//   THE ROW MODEL   the catalogue (`state/device-key-selection.ts`) crossed
//                   with what this device stores, what the manifest says, what
//                   the server bag holds and what this device has DONE with
//                   each key. Three states per row: on this device / not on
//                   this device / new on the server.
//   THE WORDING     §9.3's two use-record sentences and the fallback. The
//                   distinction is the point: for an ORACLE key CID performed
//                   the operation and knows exactly what happened; for a COPY
//                   the bytes left the tab and all CID can honestly report is
//                   the hand-over.
//   THE TWO ACTIONS untick (free — delete the copy, record the decision) and
//                   tick (costs a supersecret — load through the on-demand
//                   loader and keep the result), both over injected deps.
//
// ── the asymmetry this file exists to keep honest ───────────────────────────
// Removal is free and addition is not (§1 item 13), and NEITHER of them takes
// back a copy a module already holds. `mail_encrypt` leaves this tab by design
// (`export-mail-encrypt`), and so does the bitcash wallet DOCUMENT; unticking
// the row stops future hand-overs from this device and nothing else. The two
// sentences below are what keep "not on this device" from being read as
// "revoked" (§12).

import type { ShellAPI } from '@catalog-shell';
import type { KeyBagEntry } from '../crypto/keybag';
import { isDeviceStorableKeyId } from '../crypto/keybag';
import { moduleName } from '../key-grant/module-names';
import type { RequireKeyOptions } from '../key-grant/on-demand-key';
import {
    DEVICE_KEY_CATALOGUE, MANDATORY_DEVICE_KEY_IDS, allChosenDeviceKeyManifest,
    catalogueRowForKeyId, matchesDeviceKeyFamily,
    type DeviceKeyGroup, type DeviceKeyManifest,
} from '../state/device-key-selection';
import type { KeyUseEvent } from '../state/key-use-record';

// ── states and rows ─────────────────────────────────────────────────────────

/**
 * What the member reads on one row.
 *
 * `declined` is "not on this device" in every sense the pane has to draw: the
 * member unticked it, or the key simply never arrived here. The two are the
 * same fact for this group — the device does not hold it and the way to change
 * that is the same tick.
 */
export type DeviceKeyRowState = 'on-device' | 'declined' | 'new';

/** One row of the group, ready to draw. */
export interface DeviceKeyRow {
    /** The id this row GOVERNS: a catalogue key id, the agent family glob, or
     *  the raw id of a bag entry no catalogue row knows. It is what the two
     *  actions and the use record are keyed by. */
    id: string;
    label: string;
    group: DeviceKeyGroup;
    description: string;
    /** Required on any device that stores keys at all — no toggle (§1 item 10). */
    mandatory: boolean;
    /** The key's BYTES leave this tab when it is used (§2 item 14), so its
     *  record is a hand-over and its row carries {@link COPIED_KEY_NOTE}. */
    copied: boolean;
    state: DeviceKeyRowState;
    /** The last thing this device did with the key, or null. */
    use: KeyUseEvent | null;
    /** {@link describeKeyUse}`(use)` — the one line under the row. */
    useLine: string;
}

/** What the pane knows when it builds the group. */
export interface DeviceKeyRowsInput {
    /** Key ids this device stores — `getStoredKeyEntries(cid).map(e => e.keyId)`. */
    storedKeyIds: readonly string[];
    /** Key ids the SERVER bag holds. An empty list (the fetch failed) simply
     *  means no row can be called "new", which is the honest degradation. */
    serverKeyIds: readonly string[];
    /** This device's manifest, or null when it has none (→ everything chosen). */
    manifest: DeviceKeyManifest | null;
    /** `readKeyUseRecord(cid)`. */
    useRecord: Record<string, KeyUseEvent>;
}

/**
 * The keys whose bytes leave this tab (§2 item 14). Exactly one catalogue row
 * today: `mail_encrypt`, handed to the mail module by `export-mail-encrypt`.
 * `xrpl_data_key` — the only `release` key there has ever been — is gone by
 * decision (§2 item 15) and is deliberately NOT listed here.
 */
export const COPIED_KEY_IDS: readonly string[] = ['mail_encrypt'];

/** The fixed sentence under a copied key's row (§9.3). It exists so that
 *  "not on this device" is never read as "revoked" (§12). */
export const COPIED_KEY_NOTE =
    'Removing this key from the device does not take back the copy that module holds — '
    + 'revoke it in Connected Modules.';

/** The same point for the bitcash wallet DOCUMENT, which this group does not
 *  list because it is a blob and not a bag key (§9.3, last sentence). */
export const WALLET_DOCUMENT_NOTE =
    'Your wallets are a stored document rather than a key, so the copy Bitcash holds is '
    + 'shown in Connected Modules and not here — and removing it from this device would '
    + 'not take that copy back either.';

/** Group order for drawing. Catalogue order is already group-contiguous; this
 *  names the tail the catalogue has no rows for. */
export const OTHER_GROUP: DeviceKeyGroup = 'Other';

/**
 * The group's rows, in display order: the catalogue as it stands, then one
 * "Other" row per bag id no catalogue row knows (§7.2).
 *
 * A key that must never reach a device (`recovery_kit`) is not a row at any
 * price — offering it would be offering something the storage layer refuses.
 */
export function buildDeviceKeyRows(input: DeviceKeyRowsInput): DeviceKeyRow[] {
    const out: DeviceKeyRow[] = [];

    for (const row of DEVICE_KEY_CATALOGUE) {
        const id = row.keyId ?? row.family!;
        if (row.keyId !== undefined && !isDeviceStorableKeyId(row.keyId)) continue;
        out.push(makeRow({
            id,
            label: row.label,
            group: row.group,
            description: row.description,
            mandatory: row.mandatory,
            copied: row.keyId !== undefined && COPIED_KEY_IDS.includes(row.keyId),
        }, input));
    }

    for (const id of otherIds(input)) {
        out.push(makeRow({
            id,
            label: id,
            group: OTHER_GROUP,
            description: 'A key this version of Catalog.ID does not know by name.',
            mandatory: false,
            copied: false,
        }, input));
    }

    return out;
}

/** Bag ids — stored here or held by the server — that no catalogue row knows,
 *  sorted so the group draws the same way twice. */
function otherIds(input: DeviceKeyRowsInput): string[] {
    const seen = new Set<string>();
    for (const id of [...input.storedKeyIds, ...input.serverKeyIds]) {
        if (!isDeviceStorableKeyId(id)) continue;
        if (catalogueRowForKeyId(id) !== null) continue;
        seen.add(id);
    }
    return Array.from(seen).sort();
}

function makeRow(
    base: Omit<DeviceKeyRow, 'state' | 'use' | 'useLine'>,
    input: DeviceKeyRowsInput,
): DeviceKeyRow {
    const use = input.useRecord[base.id] ?? null;
    return {
        ...base,
        state: rowState(base.id, input),
        use,
        useLine: describeKeyUse(use),
    };
}

/**
 * On this device / not on this device / new on the server.
 *
 * "New" is narrow on purpose: UNDECIDED in the manifest (the member has never
 * been asked about it) AND present in the server bag. A chosen id the device
 * does not hold yet is a lagging copy the reconcile will fetch, not a decision
 * the member owes; an undecided id the bag does not hold yet — `xrpl_master`
 * before the ceremony mints it — is not "new on the server" either.
 */
function rowState(id: string, input: DeviceKeyRowsInput): DeviceKeyRowState {
    if (input.storedKeyIds.some(stored => governs(id, stored))) return 'on-device';
    if (isUndecided(input.manifest, id)
        && input.serverKeyIds.some(server => governs(id, server))) return 'new';
    return 'declined';
}

/** Does the row id `rowId` govern the bag id `keyId`? Exact, or the family
 *  glob's member. */
function governs(rowId: string, keyId: string): boolean {
    return rowId === keyId || (rowId.includes('*') && matchesDeviceKeyFamily(rowId, keyId));
}

/** Has the member never decided about this row? An absent manifest is not
 *  "undecided": it means every key is chosen (§7.1), which is what a device set
 *  up before the picker shipped has. */
function isUndecided(manifest: DeviceKeyManifest | null, id: string): boolean {
    if (!manifest) return false;
    if (MANDATORY_DEVICE_KEY_IDS.includes(id)) return false;
    return !listed(manifest.chosen, id) && !listed(manifest.declined, id);
}

/** The same membership rule `isDeviceKeyChosen` applies: an exact id, or a
 *  glob in the list that matches it. */
function listed(list: readonly string[], id: string): boolean {
    return list.some(x => x === id || (x.includes('*') && matchesDeviceKeyFamily(x, id)));
}

// ── what the member reads ───────────────────────────────────────────────────

/** The status text for one row. */
export function deviceKeyStateLabel(state: DeviceKeyRowState): string {
    switch (state) {
        case 'on-device': return 'On this device';
        case 'new': return 'New — on your account, not on this device';
        default: return 'Not on this device';
    }
}

/** The toggle's label for one row. */
export function deviceKeyActionLabel(state: DeviceKeyRowState): string {
    return state === 'on-device' ? 'Remove from this device' : 'Keep on this device';
}

/**
 * The one line under a row (§9.3), in one of two wordings.
 *
 * An ORACLE use says what THIS DEVICE did, because CID performed the operation
 * and handed back only the result. A COPY says only that the bytes were handed
 * over, because after that CID knows nothing — and wording it as "last used"
 * would be a claim this tab cannot make.
 */
export function describeKeyUse(use: KeyUseEvent | null): string {
    if (!use) return 'Not used on this device';
    const when = formatUseDate(use.at);
    const who = moduleName(use.requesterId);
    return use.kind === 'copy'
        ? `Copy handed to ${who} on ${when}`
        : `Last used here ${when} by ${who} (${use.mode})`;
}

/** A stamp this tab wrote, rendered in the member's locale. A value that does
 *  not parse is shown verbatim rather than as "Invalid Date": the event itself
 *  is still true. */
function formatUseDate(at: string): string {
    const date = new Date(at);
    return Number.isNaN(date.getTime()) ? at : date.toLocaleDateString();
}

// ── the two actions ─────────────────────────────────────────────────────────

/** The one phrase the on-demand loader shows for a tick: the member asked for
 *  the key to be kept, not for an operation that happens to need it.
 *
 *  Lowercase and verb-first, because the loader reads it INSIDE a sentence —
 *  "…and it is needed to ${purpose}." (desktop-setup §6.5). */
export const KEEP_KEY_PURPOSE = 'keep this key on this device';

/** Ticking needs this device's encryption key, and a supersecret-only session
 *  does not hold one (§9.3). */
export const SUPERSECRET_ONLY_NOTE =
    'Adding a key back needs this device\'s encryption key, which this session does not '
    + 'hold. Unlock this device with its PIN or Authenticator code and try again.';

/** The loader answered with nothing: the bag holds no such key, or the
 *  supersecret prompt was dismissed (§9.1). */
export const KEY_NOT_LOADED_NOTE =
    'That key was not loaded — your account holds no copy of it, or the supersecret '
    + 'prompt was closed. Nothing on this device changed.';

/** A family row whose family has no members in the bag. */
export const NOTHING_TO_KEEP_NOTE =
    'Your account holds no key of this kind yet, so there is nothing to keep here.';

/** A tab whose bag is locked cannot load anything (the loader's rule 1). */
export const LOCKED_NOTE =
    'Your Catalog.ID is locked, so this key cannot be loaded. Unlock it and try again.';

/** What the two actions need. Every one of them is injected: this module never
 *  reads storage, never fetches and never opens a dialog. */
export interface DeviceKeyActionDeps {
    /** The identity whose device copy is being changed. */
    cid: string;
    /** Forwarded to the loader, which owns the supersecret prompt. */
    shell: ShellAPI;
    /** Key ids the server bag holds — how a family row finds its members. */
    serverKeyIds: readonly string[];
    /** `key-grant/on-demand-key.ts` `requireKey`. */
    requireKey(keyId: string, opts: RequireKeyOptions): Promise<KeyBagEntry[]>;
    /** `state/device-slots.ts` `getSlotKey` — null in a supersecret-only session. */
    getSlotKey(cid: string): Uint8Array | null;
    /** `state/device-storage.ts` `removeStoredKeyEntries`. */
    removeStoredKeyEntries(cid: string, keyId: string): void;
    /** `state/device-storage.ts` `getStoredKeyEntries`, read FRESH — the loader
     *  may have imported something between the pane's paint and this call. */
    listStoredEntries(cid: string): readonly { keyId: string; keyIndex: number }[];
    /** `state/device-storage.ts` `encryptAndStoreKey`, adapted to one entry. */
    storeEntry(cid: string, entry: KeyBagEntry, dek: Uint8Array): Promise<void>;
    readManifest(cid: string): DeviceKeyManifest | null;
    writeManifest(cid: string, manifest: DeviceKeyManifest): void;
}

/** What one action did, and the one line to show for it. */
export type DeviceKeyChange =
    | { status: 'kept' }
    | { status: 'removed' }
    | { status: 'unchanged'; note: string };

/** Can this session put a key BACK on the device? Removal never needs the
 *  device key; an import encrypts under it, so without one there is nothing to
 *  encrypt under and the tick is disabled with {@link SUPERSECRET_ONLY_NOTE}. */
export function canKeepKeysOnDevice(deps: DeviceKeyActionDeps): boolean {
    return deps.getSlotKey(deps.cid) !== null;
}

/**
 * Untick: this device stops holding `id`.
 *
 * Free, immediate and unprompted — the whitepaper's "at any time" (§3.4.7).
 * Every generation of the key goes, and the manifest records the decision so
 * the reconcile stops reporting it missing on every unlock (§7.3, §12).
 *
 * An absent manifest starts from "everything chosen", not from empty: writing
 * only the decline would leave every other id UNDECIDED and the pane would
 * suddenly call them all new.
 *
 * @throws Error for a mandatory id — those rows have no toggle (§1 item 10).
 */
export function untickDeviceKey(id: string, deps: DeviceKeyActionDeps): DeviceKeyChange {
    assertOptional(id);
    deps.removeStoredKeyEntries(deps.cid, id);
    const manifest = deps.readManifest(deps.cid) ?? allChosenDeviceKeyManifest();
    deps.writeManifest(deps.cid, {
        v: 1,
        chosen: manifest.chosen.filter(x => x !== id),
        declined: [...manifest.declined, id],
    });
    return { status: 'removed' };
}

/**
 * Tick: load `id` from the key bag and keep it here.
 *
 * The loader owns the supersecret prompt and the import; the offer is
 * PRE-ACCEPTED (`confirmImport`) because ticking the row already was the
 * answer, and asking twice would be theatre.
 *
 * Two things this function owns that the loader cannot:
 *  * a FAMILY row is expanded against the server bag, because
 *    `agent:*:agent_signing_key` is one toggle over many real ids;
 *  * the durable OUTCOME. The loader short-circuits on an in-memory chain — a
 *    key loaded once this session and not kept — and its import offer never
 *    runs on that path. After a `kept` answer the device holds the key, so the
 *    missing generations are stored here.
 *
 * Never throws for a runtime condition: a locked bag, a dismissed prompt and
 * an absent key all come back as `unchanged` with a line to show.
 *
 * @throws Error for a mandatory id — as {@link untickDeviceKey}.
 */
export async function tickDeviceKey(
    id: string,
    deps: DeviceKeyActionDeps,
): Promise<DeviceKeyChange> {
    assertOptional(id);

    const dek = deps.getSlotKey(deps.cid);
    // Asked before anything is loaded: a supersecret prompt whose result could
    // not be kept is a cost with no outcome.
    if (!dek) return { status: 'unchanged', note: SUPERSECRET_ONLY_NOTE };

    const targets = id.includes('*')
        ? deps.serverKeyIds.filter(keyId => matchesDeviceKeyFamily(id, keyId))
        : [id];
    if (targets.length === 0) return { status: 'unchanged', note: NOTHING_TO_KEEP_NOTE };

    const loaded: KeyBagEntry[] = [];
    for (const target of targets) {
        let entries: KeyBagEntry[];
        try {
            entries = await deps.requireKey(target, {
                shell: deps.shell,
                purpose: KEEP_KEY_PURPOSE,
                import: 'ask',
                cid: deps.cid,
                confirmImport: async () => true,
            });
        } catch (err) {
            return { status: 'unchanged', note: loadFailureNote(err) };
        }
        loaded.push(...entries);
    }
    if (loaded.length === 0) return { status: 'unchanged', note: KEY_NOT_LOADED_NOTE };

    const stored = new Set(
        deps.listStoredEntries(deps.cid).map(e => `${e.keyId}@${e.keyIndex}`));
    for (const entry of loaded) {
        if (stored.has(`${entry.keyId}@${entry.keyIndex ?? 0}`)) continue;
        await deps.storeEntry(deps.cid, entry, dek);
    }

    const manifest = deps.readManifest(deps.cid) ?? allChosenDeviceKeyManifest();
    deps.writeManifest(deps.cid, {
        v: 1,
        chosen: [...manifest.chosen, id],
        declined: manifest.declined.filter(x => x !== id),
    });
    return { status: 'kept' };
}

function loadFailureNote(err: unknown): string {
    const message = err instanceof Error ? err.message : String(err);
    if (message === 'cid-locked') return LOCKED_NOTE;
    return `That key could not be loaded: ${message}`;
}

/** The mandatory rows carry no toggle, so reaching either action with one is a
 *  programming error — the same posture the loader takes (§9.1 rule 2). */
function assertOptional(id: string): void {
    if (MANDATORY_DEVICE_KEY_IDS.includes(id)) {
        throw new Error(
            `'${id}' is a mandatory device key: every device that stores keys at all holds `
            + 'it (device-setup-restructure §1 item 10), so its row has no toggle');
    }
}
